import type { FileWithPath } from '@mantine/dropzone'
import { notifications } from '@mantine/notifications'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'

import { needsExtractionMimeTypes, supportsProgressMimeTypes, type UploadPhase } from '@/components/FileUpload'
import { useFileUpload } from '@/hooks/useFileUpload'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { CreateStepParams, GetResourcesResponse, ProcessProgress } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'
import { castStepToZodForm } from '@/utils/contentModuleForm'

import type { CMFormSection } from './ContentModuleFormScreen'
import type { FAQSectionRef } from './FAQSection'

type DescriptionAIState = 'idle' | 'loading' | 'pending'

export function useStepModalState(props: CMFormSection) {
  const { uiStore, api } = useGlobalState()

  // Website extraction state
  const [isExtracting, setIsExtracting] = useState(false)
  const [websiteUrl, setWebsiteUrl] = useState('')

  // Pending changes state (saved on "Save and Exit")
  const [pendingFileName, setPendingFileName] = useState<string | null>(null)
  const [showInApp, setShowInApp] = useState(false)
  const [pendingDescription, setPendingDescription] = useState<string | null>(null)

  // AI generation state for source file description
  const [descriptionAIState, setDescriptionAIState] = useState<DescriptionAIState>('idle')
  const [descriptionSuggestion, setDescriptionSuggestion] = useState<string | null>(null)

  // Upload phase tracking - explicit state machine to prevent UI flashing
  const [uploadPhase, setUploadPhase] = useState<UploadPhase>('idle')
  const [processProgressPct, setProcessProgressPct] = useState(0)

  // AbortController for step creation
  const [stepCreationAbortController, setStepCreationAbortController] = useState<AbortController | null>(null)

  // Track created step ID for subsequent uploads within the same modal session
  const [createdStepId, setCreatedStepId] = useState<string | null>(null)
  const createdStepIdRef = useRef<string | null>(null)

  // Effective step ID - use props.stepId or the step we created in this session
  const effectiveStepId = props.stepId || createdStepId

  // Query step resources directly from endpoint to include source files
  // Note: api.getResources filters out source files, so we call the endpoint directly
  const stepResourcesQuery = useQuery({
    queryKey: ['step', 'resources', 'all', effectiveStepId],
    queryFn: async () => {
      const response = await api.request<GetResourcesResponse>('/cm/get_resources', 'get', {
        step_id: effectiveStepId!,
      })
      return response
    },
    enabled: !!effectiveStepId,
  })

  // Refs for tracking auto-generation
  const autoGenerationTriggeredRef = useRef<string | null>(null)
  const descriptionAutoGenTriggeredRef = useRef<string | null>(null)
  const faqSectionRef = useRef<FAQSectionRef>(null)

  const {
    uploadProgress,
    upload: uploadContent,
    cancel: cancelContentUpload,
  } = useFileUpload({
    onPrepareError: () => {
      props.stepForm.setFieldError('content.file', 'Something went wrong. Please try again or contact support.')
      setUploadPhase('idle')
    },
    onUploadError: () => {
      props.stepForm.setFieldError('content.file', 'Upload failed. Please try again or contact support.')
      setUploadPhase('idle')
    },
    onUploadSuccess: () => props.stepForm.setFieldError('content.file', undefined),
  })

  function onProgress(progress: ProcessProgress['processProgress']) {
    const current = progress?.current ?? 0
    const total = progress?.total ?? 0
    const fraction = current / total || 0
    const progressPct = fraction * 100
    setProcessProgressPct(progressPct)
  }

  async function createStepWithAbortSupport(params: CreateStepParams) {
    const abortController = new AbortController()
    setStepCreationAbortController(abortController)

    try {
      const response = await api.createStep(params, abortController.signal)

      // Track created step ID for subsequent uploads
      setCreatedStepId(response.stepId)
      createdStepIdRef.current = response.stepId

      notifications.show({
        title: 'File Created',
        message: 'The file was successfully created.',
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })

      const steps = await props.stepsQuery.refetch()
      const step = steps.data?.find(step => step.stepId === response.stepId)

      if (step) {
        props.stepForm.setValues(castStepToZodForm(step))
      }

      return response
    } catch (error) {
      if (abortController.signal.aborted) {
        console.log('[StepModal] File creation was cancelled')
        return null
      }

      notifications.show({
        title: 'Something went wrong.',
        message: 'There was an issue creating the file. Please try again or contact support.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      throw error
    } finally {
      setStepCreationAbortController(null)
    }
  }

  async function handleExtractFromWebsite() {
    if (!websiteUrl.trim()) return

    setIsExtracting(true)
    setUploadPhase('processing')
    setProcessProgressPct(50)

    // Use ref to get current step ID (avoids stale closure)
    const stepId = props.stepId || createdStepIdRef.current

    try {
      if (stepId) {
        await props.contentUpdateMutation?.mutateAsync({
          step_id: stepId,
          url: websiteUrl,
          file_name: websiteUrl,
          use_llm_for_scraping: false,
        })

        // Invalidate all step-related queries to clear stale cache
        await api.queryClient.invalidateQueries({ queryKey: ['step', 'content', stepId] })
        await api.queryClient.invalidateQueries({ queryKey: ['step', 'resources', 'all', stepId] })
        await api.queryClient.invalidateQueries({ queryKey: ['steps', props.taskId] })

        // Refetch to get fresh data
        await Promise.all([
          api.queryClient.refetchQueries({ queryKey: ['step', 'content', stepId] }),
          api.queryClient.refetchQueries({ queryKey: ['step', 'resources', 'all', stepId] }),
          api.queryClient.refetchQueries({ queryKey: ['steps', props.taskId] }),
        ])
      } else {
        const lastStep = props.stepsQuery.data?.[props.stepsQuery.data.length - 1]
        const params: CreateStepParams = {
          task_id: props.taskId!,
          url: websiteUrl,
          file_name: websiteUrl,
          use_llm_for_scraping: false,
          preceding_step_id: lastStep?.stepId,
        }
        await createStepWithAbortSupport(params)
        uiStore.setPendingStepDeletion(true)
      }
    } catch (error) {
      console.error('Error extracting from website:', error)
      notifications.show({
        title: 'Extraction Failed',
        message: 'Could not start content extraction. Please check the URL or try again later.',
        color: 'red',
      })
    } finally {
      setIsExtracting(false)
      setUploadPhase('idle')
      setProcessProgressPct(0)
    }
  }

  async function processDroppedFiles(files: FileWithPath[]) {
    const file = files[0]

    // Phase 1: Uploading to S3
    setUploadPhase('uploading')
    const uploadResponse = await uploadContent(file)

    if (!uploadResponse) {
      setUploadPhase('idle')
      return
    }

    // Reset pending file name since we're uploading new content
    setPendingFileName(null)

    // Phase 2: Processing on backend
    setUploadPhase('processing')
    setProcessProgressPct(0)

    // Use ref to get current step ID (avoids stale closure)
    const stepId = props.stepId || createdStepIdRef.current

    try {
      if (stepId) {
        if (needsExtractionMimeTypes.includes(file.type)) {
          if (supportsProgressMimeTypes.includes(file.type)) {
            await props.contentUpdateMutation?.mutateAsync({
              step_id: stepId,
              file_name: file.name,
              s3_file: uploadResponse.fileName,
              onProgress,
            })
          } else {
            setProcessProgressPct(50)
            await props.contentUpdateMutation?.mutateAsync({
              step_id: stepId,
              file_name: file.name,
              s3_file: uploadResponse.fileName,
            })
          }
        } else {
          // For media files, poll until any existing processing completes, then update
          setProcessProgressPct(50)

          let isProcessing = true
          while (isProcessing) {
            const content = await api.getContent({ step_id: stepId })
            isProcessing = content.inProcess ?? false
            if (isProcessing) {
              await new Promise(resolve => setTimeout(resolve, 2000))
            }
          }

          await props.contentUpdateMutation?.mutateAsync({
            step_id: stepId,
            file_name: file.name,
            s3_file: uploadResponse.fileName,
          })
        }

        // Clear the name field so AI can regenerate based on new content
        props.stepForm.setFieldValue('name', '')
        props.stepForm.clearFieldError('name')

        // Reset auto-generation tracking so it triggers for the new file
        autoGenerationTriggeredRef.current = null
        descriptionAutoGenTriggeredRef.current = null

        // Reset description state so it regenerates if "Make Available in App" is enabled
        setPendingDescription(null)
        setDescriptionAIState('idle')
        setDescriptionSuggestion(null)

        // Reset FAQ state so the FAQ section reinitializes with one empty slot (no auto-generate)
        props.faqActions.resetStepFAQState()

        // Invalidate all step-related queries to clear stale cache
        await api.queryClient.invalidateQueries({ queryKey: ['step', 'content', stepId] })
        await api.queryClient.invalidateQueries({ queryKey: ['step', 'resources', 'all', stepId] })
        await api.queryClient.invalidateQueries({ queryKey: ['steps', props.taskId] })

        // Refetch step content, resources, and steps list to get fresh data
        await Promise.all([
          api.queryClient.refetchQueries({ queryKey: ['step', 'content', stepId] }),
          api.queryClient.refetchQueries({ queryKey: ['step', 'resources', 'all', stepId] }),
          api.queryClient.refetchQueries({ queryKey: ['steps', props.taskId] }),
        ])
      } else {
        const lastStep = props.stepsQuery.data?.[props.stepsQuery.data.length - 1]

        if (needsExtractionMimeTypes.includes(file.type)) {
          if (supportsProgressMimeTypes.includes(file.type)) {
            await createStepWithAbortSupport({
              task_id: props.taskId!,
              file_name: file.name,
              s3_file: uploadResponse.fileName,
              onProgress,
            })
          } else {
            setProcessProgressPct(50)
            await createStepWithAbortSupport({
              task_id: props.taskId!,
              file_name: file.name,
              s3_file: uploadResponse.fileName,
            })
          }
        } else {
          setProcessProgressPct(50)
          await createStepWithAbortSupport({
            task_id: props.taskId!,
            preceding_step_id: lastStep?.stepId,
            file,
          })
        }
        uiStore.setPendingStepDeletion(true)
      }
    } finally {
      setUploadPhase('idle')
    }
  }

  function cancelStepCreation() {
    if (stepCreationAbortController) {
      console.log('[StepModal] Cancelling step creation...')
      stepCreationAbortController.abort()
      setStepCreationAbortController(null)
    }
  }

  async function generateSourceFileDescription() {
    // Use ref to get current step ID (avoids stale closure)
    const stepId = props.stepId || createdStepIdRef.current
    if (!stepId) return

    setDescriptionAIState('loading')

    try {
      const response = await api.safeRequest(api.generate, {
        step_id: stepId,
        system_prompt_template_id: 'generate_cm_file_description',
        message_template_id: 'generate_step_message',
        extra_data: {
          accepted_data: pendingDescription || undefined,
        },
      })

      if (response?.ok && response.data) {
        const suggestion = (response.data as string).trim().replace(/\n\s*\n/g, '\n\n')
        setDescriptionSuggestion(suggestion)
        setDescriptionAIState('pending')
      } else {
        setDescriptionAIState('idle')
      }
    } catch {
      setDescriptionAIState('idle')
    }
  }

  async function generateSourceFileDescriptionWithGuide(instructions: string) {
    const stepId = props.stepId || createdStepIdRef.current
    if (!stepId) return

    setDescriptionAIState('loading')

    try {
      const extraData: Record<string, string | undefined> = {
        accepted_data: pendingDescription || undefined,
        user_instructions: instructions.trim() || undefined,
      }
      if (descriptionAIState === 'pending' && descriptionSuggestion) {
        extraData.proposed_data = descriptionSuggestion
      }

      const response = await api.safeRequest(api.generate, {
        step_id: stepId,
        system_prompt_template_id: 'generate_cm_file_description',
        message_template_id: 'generate_step_message',
        extra_data: extraData,
      })

      if (response?.ok && response.data) {
        const suggestion = (response.data as string).trim().replace(/\n\s*\n/g, '\n\n')
        setDescriptionSuggestion(suggestion)
        setDescriptionAIState('pending')
      } else {
        setDescriptionAIState('idle')
      }
    } catch {
      setDescriptionAIState('idle')
    }
  }

  function acceptDescriptionSuggestion() {
    if (descriptionSuggestion) {
      setPendingDescription(descriptionSuggestion)
      setDescriptionSuggestion(null)
      setDescriptionAIState('idle')
    }
  }

  function rejectDescriptionSuggestion() {
    setDescriptionSuggestion(null)
    setDescriptionAIState('idle')
  }

  // Derived state
  const stepResources = stepResourcesQuery.data
  const isStepCreating = !!stepCreationAbortController
  const isSectionContentMutating =
    props.stepCreateMutation.isPending || props.contentUpdateMutation.isPending || isStepCreating
  const hasContent = !!props.stepContentQuery.data
  const isMutating =
    props.stepUpdateMutation.isPending || isSectionContentMutating || isExtracting || uploadPhase !== 'idle'
  const contentLoading = uploadPhase !== 'idle' || isSectionContentMutating || props.stepContentQuery.isFetching

  // Find uploaded file resource (not text or url) - used for file name preview
  const uploadedFileResource = stepResources?.find((r: any) => r.isSourceFile && r.type !== 'text' && r.type !== 'url')

  // Find any source file resource - used for "Make Available" section
  const sourceFileResource = useMemo(() => {
    const resource = stepResources?.find((r: any) => r.isSourceFile)
    if (!resource) return undefined

    // TEMPORARY: Force showInAppSupported to true for development
    if (resource.showInAppSupported === false) {
      console.warn('[useStepModalState] Forcing showInAppSupported to true (was false)')
    }
    return { ...resource, showInAppSupported: true }
  }, [stepResources])

  // Seed file description suggestion from task_steps descriptionAi when description is blank (no extra API call)
  useEffect(() => {
    if (!effectiveStepId || !props.stepsQuery?.data) return

    const step = props.stepsQuery.data.find(s => s.stepId === effectiveStepId)
    if (!step?.descriptionAi?.trim()) return

    const descriptionBlank = !(pendingDescription?.trim() || sourceFileResource?.description?.trim())
    if (!descriptionBlank) return

    if (descriptionAIState !== 'idle') return

    setDescriptionSuggestion(step.descriptionAi.trim())
    setDescriptionAIState('pending')
  }, [effectiveStepId, props.stepsQuery?.data, pendingDescription, sourceFileResource?.description, descriptionAIState])

  return {
    // Queries
    stepResources,

    // Website extraction
    isExtracting,
    websiteUrl,
    setWebsiteUrl,
    handleExtractFromWebsite,

    // Pending changes
    pendingFileName,
    setPendingFileName,
    showInApp,
    setShowInApp,
    pendingDescription,
    setPendingDescription,

    // Description AI
    descriptionAIState,
    descriptionSuggestion,
    generateSourceFileDescription,
    generateSourceFileDescriptionWithGuide,
    acceptDescriptionSuggestion,
    rejectDescriptionSuggestion,

    // Upload phase tracking
    uploadPhase,
    uploadProgress,
    processProgressPct,

    // File upload
    cancelContentUpload,
    processDroppedFiles,

    // Step creation
    isStepCreating,
    cancelStepCreation,

    // Refs
    autoGenerationTriggeredRef,
    descriptionAutoGenTriggeredRef,
    faqSectionRef,

    // Derived state
    isSectionContentMutating,
    hasContent,
    isMutating,
    contentLoading,
    uploadedFileResource,
    sourceFileResource,
  }
}
